'use client'
import { RegisterUserSession } from "@/services/microservices/user.service";
import { eLoginType } from "../models/enum/loginType";
import { iBankIDLoginModel } from "../models/loginModels/bankIDLoginModel";
import { iLoginModel } from "../models/loginModels/loginModel";
import { iLoginResponse } from "../models/loginModels/loginResponse";
import { iMitIDLoginModel } from "../models/loginModels/mitIDLoginModel";
import { logError, logInfo } from "./azureBlobLogger";


export {
    mustBe18Validation as MustBe18Validation,
    uniqueIdentifierValidation as UniqueIdentifierValidation,
    organisationUniqueIdentifierValidation as OrganisationUniqueIdentifierValidation
};

function mustBe18Validation(dateOfBirth: string, country: string, isPersonalNumber: boolean = false): boolean {
    switch (country.toLowerCase()) {
        case "se":
        case "sv":
            if (isPersonalNumber) {
                return mustBe18ValidationPersonalNumberSE(dateOfBirth); //not actually date of birth, but personal number
            } else {
                //Right now we are using date of birth instead of personal number as all different countries return the same format for the date of birth
                return defaultMustBe18ValidationDateOfBirth(dateOfBirth);
            }
        case "dk":
        case "da":
            if (isPersonalNumber) {
                return mustBe18ValidationCPR(dateOfBirth); //not actually date of birth, but personal number (CPR)
            } else {
                //Right now we are using date of birth instead of personal number as all different countries return the same format for the date of birth
                return defaultMustBe18ValidationDateOfBirth(dateOfBirth);
            }
        case "no":
        case "nb":
            // return mustBe18ValidationPersonalNumberNO(uniqueIdentifier);
            //Right now we are using date of birth instead of personal number as all different countries return the same format for the date of birth
            return defaultMustBe18ValidationDateOfBirth(dateOfBirth);

    }

    logError("validationHelper.ts", "mustBe18Validation", "Country not supported", "country", country);
    return false;
}

function defaultMustBe18ValidationDateOfBirth(dateOfBirth: string): boolean {
    const dateParts = dateOfBirth.split("-");
    if (dateParts.length !== 3) {
        return false;
    }

    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);

    const birthDate = new Date(year, month - 1, day);

    var age = new Date().getFullYear() - birthDate.getFullYear();
    const monthDiff = new Date().getMonth() - birthDate.getMonth();
    const dayDiff = new Date().getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }

    return age >= 18;
}

function mustBe18ValidationCPR(cpr: string): boolean {
    const day = parseInt(cpr.substring(0, 2), 10);
    const month = parseInt(cpr.substring(2, 4), 10);
    const year = parseInt(cpr.substring(4, 6), 10);
    const centuryIndicator = parseInt(cpr.charAt(6), 10);

    let fullYear;

    if (centuryIndicator >= 0 && centuryIndicator <= 3) {
        fullYear = 1900 + year;
    } else if (centuryIndicator === 4) {
        if (year >= 0 && year <= 36) {
            fullYear = 2000 + year;
        } else {
            fullYear = 1900 + year;
        }
    } else if (centuryIndicator >= 5 && centuryIndicator <= 8) {
        if (year >= 0 && year <= 57) {
            fullYear = 2000 + year;
        } else {
            fullYear = 1800 + year;
        }
    } else if (centuryIndicator === 9) {
        if (year >= 0 && year <= 36) {
            fullYear = 2000 + year;
        } else {
            fullYear = 1900 + year;
        }
    } else {
        throw new Error("Invalid CPR number");
    }

    const birthDate = new Date(fullYear, month - 1, day);

    var age = new Date().getFullYear() - birthDate.getFullYear();
    const monthDiff = new Date().getMonth() - birthDate.getMonth();
    const dayDiff = new Date().getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }

    return age >= 18;
}

function mustBe18ValidationPersonalNumberSE(personalNumber: string): boolean {
    const day = parseInt(personalNumber.substring(6, 8), 10);
    const month = parseInt(personalNumber.substring(4, 6), 10);
    const year = parseInt(personalNumber.substring(0, 4), 10);
    const birthDate = new Date(year, month - 1, day);

    var age = new Date().getFullYear() - birthDate.getFullYear();
    const monthDiff = new Date().getMonth() - birthDate.getMonth();
    const dayDiff = new Date().getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }

    return age >= 18;
}

function mustBe18ValidationPersonalNumberNO(personalNumber: string): boolean {
    //TODO: UPDATE TO NORWEGIAN VALIDATION
    const day = parseInt(personalNumber.substring(6, 8), 10);
    const month = parseInt(personalNumber.substring(4, 6), 10);
    const year = parseInt(personalNumber.substring(0, 4), 10);
    const birthDate = new Date(year, month - 1, day);

    var age = new Date().getFullYear() - birthDate.getFullYear();
    const monthDiff = new Date().getMonth() - birthDate.getMonth();
    const dayDiff = new Date().getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }

    return age >= 18;
}

function uniqueIdentifierValidation(uniqueIdentifier: string, country: string): boolean {
    if (!/^\d+$/.test(uniqueIdentifier)) {
        return false;
    }

    switch (country.toLowerCase()) {
        case "se":
        case "sv":
            return personalNumberSEValidation(uniqueIdentifier);
        case "dk":
        case "da":
            return cprValidation(uniqueIdentifier);
        case "no":
        case "nb":
            return personalNumberNOValidation(uniqueIdentifier);
    }

    logError("validationHelper.ts", "mustBe18Validation", "Country not supported", "country", country);
    return false;
}

function cprValidation(cpr: string): boolean {
    return cpr.length === 10;

    //TODO: BELOW IS A MORE CORRECT VALIDATION. It checking if the date is valid and the extra numbers are correct as well.
    // // Check if the length is 10
    // if (cpr.length !== 10) {
    //     return false;
    // }

    // // Extract the date part and the control number
    // const datePart = cpr.substring(0, 6);
    // const controlNumber = cpr.substring(6);

    // // Check if the date part is a valid date
    // const day = parseInt(datePart.substring(0, 2), 10);
    // const month = parseInt(datePart.substring(2, 4), 10);
    // const year = parseInt(datePart.substring(4, 6), 10);
    // const date = new Date(year + 1900, month - 1, day);

    // if (date.getFullYear() !== year + 1900 || date.getMonth() + 1 !== month || date.getDate() !== day) {
    //     return false;
    // }

    // // Validate the control number using the CPR algorithm
    // return cprAlgorithmValidation(cpr);
}

// function cprAlgorithmValidation(cpr: string): boolean {
//     const weights = [4, 3, 2, 7, 6, 5, 4, 3, 2, 1];
//     let sum = 0;

//     for (let i = 0; i < 10; i++) {
//         sum += parseInt(cpr.charAt(i), 10) * weights[i];
//     }

//     return sum % 11 === 0;
// }

function personalNumberSEValidation(personalNumber: string): boolean {
    if (personalNumber.length !== 12) {
        return false;
    }

    // Extract the date part and the control number
    const datePart = personalNumber.substring(0, 8);
    const controlNumber = personalNumber.substring(8);

    // Check if the date part is a valid date
    const year = parseInt(datePart.substring(0, 4), 10);
    const month = parseInt(datePart.substring(4, 6), 10);
    const day = parseInt(datePart.substring(6, 8), 10);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
        return false;
    }

    // Validate the control number using the Luhn algorithm
    return luhnAlgorithmValidation(personalNumber.substring(2));
}

function personalNumberNOValidation(personalNumber: string): boolean {
    //TODO: UPDATE TO NORWEGIAN VALIDATION
    if (personalNumber.length !== 12) {
        return false;
    }

    // Extract the date part and the control number
    const datePart = personalNumber.substring(0, 8);
    const controlNumber = personalNumber.substring(8);

    // Check if the date part is a valid date
    const year = parseInt(datePart.substring(0, 4), 10);
    const month = parseInt(datePart.substring(4, 6), 10);
    const day = parseInt(datePart.substring(6, 8), 10);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
        return false;
    }

    // Validate the control number using the Luhn algorithm
    return luhnAlgorithmValidation(personalNumber.substring(2));
}

function luhnAlgorithmValidation(number: string): boolean {
    let sum = 0;
    let shouldDouble = false;

    // Process each digit starting from the right
    for (let i = number.length - 1; i >= 0; i--) {
        let digit = parseInt(number.charAt(i), 10);

        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
}

function organisationUniqueIdentifierValidation(uniqueIdentifier: string, country: string): boolean {
    if (!/^\d+$/.test(uniqueIdentifier)) {
        return false;
    }

    switch (country.toLowerCase()) {
        case "se":
        case "sv":
            return organisationNumberSEValidation(uniqueIdentifier);
        case "dk":
        case "da":
            return cvrValidation(uniqueIdentifier);
        case "no":
        case "nb":
            return organisationNumberNOValidation(uniqueIdentifier);
    }

    logError("validationHelper.ts", "mustBe18Validation", "Country not supported", "country", country);
    return false;
}

function cvrValidation(cvr: string): boolean {
    return cvr.length === 8;

    //TODO: BELOW IS A MORE CORRECT VALIDATION. It checking if the date is valid and the extra numbers are correct as well.
    // if (cvr.length !== 8) {
    //     return false;
    // }

    // // Validate the control number using the CVR algorithm
    // return cvrAlgorithmValidation(cvr);
}

// function cvrAlgorithmValidation(cvr: string): boolean {
//     const weights = [2, 7, 6, 5, 4, 3, 2, 1];
//     let sum = 0;

//     for (let i = 0; i < 8; i++) {
//         sum += parseInt(cvr.charAt(i), 10) * weights[i];
//     }

//     return sum % 11 === 0;
// }

function organisationNumberSEValidation(organisationNumber: string): boolean {
    if (organisationNumber.length !== 10) {
        return false;
    }

    return luhnAlgorithmValidation(organisationNumber);
}

function organisationNumberNOValidation(organisationNumber: string): boolean {
    if (!/^\d{9}$/.test(organisationNumber)) {
        return false;
    }
    const weights = [3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    for (let i = 0; i < 8; i++) {
        sum += parseInt(organisationNumber.charAt(i), 10) * weights[i];
    }

    const remainder = sum % 11;
    let controlDigit = 11 - remainder;
    if (controlDigit === 11) controlDigit = 0;
    if (controlDigit === 10) return false;

    return controlDigit === parseInt(organisationNumber.charAt(8), 10);
}